import { INSTALL_KRYS_OR_CDL } from './krys';
import { INSTALL_MIA } from './mia';
import { INSTALL_OCULERA } from './oculera';
import { INSTALL_OPTICIEN } from './opticien';
import { INSTALL_ORTHO, INSTALL_ORTHO_HOME } from './ortho';
import { Command, Procedure } from './procedure.interface';
import { Device } from '@/device';
import { APK } from '@/interfaces/deal.interface';
import { InstallProperties } from '@/interfaces/properties.interface';
import { LogStatus, Logger } from '@/logger';

export type ProcedureFactory = (properties: unknown) => Procedure;

export const PROCEDURES = new Map<APK, Procedure | ProcedureFactory>([
  ['Krys', INSTALL_KRYS_OR_CDL(false)],
  ['CDL', INSTALL_KRYS_OR_CDL(true)],
  ['Opticien', INSTALL_OPTICIEN],
  ['Ortho', INSTALL_ORTHO],
  ['Ortho homme', INSTALL_ORTHO_HOME],
  ['Oculera', INSTALL_OCULERA],
  ['MIA', INSTALL_MIA],
]);

export enum ProcedureLogStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

export class ProcedureExecutor {
  private _procedure: Procedure;
  private _device: Device;
  private _progressCb: (progress: number) => void;
  private _lastCmdId: number;
  private _logger: Logger;
  private _properties: InstallProperties;

  constructor(procedureName: APK, properties: InstallProperties, device: Device, logger: Logger) {
    const procedure = PROCEDURES.get(procedureName);
    if (!procedure) {
      throw new Error('Installation procedure not found');
    }
    this._properties = properties;
    if (procedure instanceof Function) {
      this._procedure = procedure(properties.properties);
    } else {
      this._procedure = procedure;
    }
    this._device = device;
    this._progressCb = () => {};
    this._lastCmdId = 0;
    this._logger = logger;
  }

  public set progressCb(cb: (progress: number) => void) {
    this._progressCb = cb;
  }

  public async install(): Promise<boolean> {
    return this._executeProcedure(this._procedure.commands);
  }

  public async validate(): Promise<boolean> {
    return this._executeProcedure(this._procedure.validationCommands);
  }

  public async reset(): Promise<boolean> {
    return this._executeProcedure(this._procedure.resetCommands);
  }

  private async _executeProcedure(commands: Array<Command>): Promise<boolean> {
    let failed = false;
    for (let i = this._lastCmdId; i < commands.length; i++) {
      const command = commands[i];
      this._lastCmdId = i;
      this._logger.logCommand(command.toLog());
      const result = await command.execute(this._device, this._logger, this._properties);
      let status = LogStatus.SUCCESS;
      if (!result.success) {
        status = command.stopOnError ? LogStatus.ERROR : LogStatus.WARN;
      }
      this._logger.logResult(result.message, status);
      this._progressCb(((i + 1) * 100) / commands.length);
      if (!result.success && command.stopOnError) {
        failed = true;
        break;
      }
    }
    return !failed;
  }
}
